import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/kids-getting-back-school-together_23-2149507625.jpg';

function AfterSchoolPrograms() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (consistent with other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Program Structure & Daily Schedule',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Typical flow:', 'Snack → homework/academic time → enrichment blocks (arts/STEM/sports) → closing circle.'],
            ['Ratios & groups:', 'Age-banded groups with appropriate staff-to-student ratios for safety and supervision.'],
            ['Attendance & checkout:', 'Roster check-ins, authorized pickup lists, and secure sign-out procedures.'],
          ].map(([k, v], i) => (
            <li key={`struct-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Enrollment, Eligibility & Fees',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Who can enroll:', 'Elementary and middle school students; priority may apply for working families.'],
            ['Docs needed:', 'Emergency contacts, medical info, allergy details, and authorized pickup forms.'],
            ['Cost & aid:', 'Sliding-scale fees and limited scholarships where available; sibling discounts at select sites.'],
          ].map(([k, v], i) => (
            <li key={`enroll-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Academic Support',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Homework help:', 'Quiet study time with staff/volunteer tutors and access to learning materials.'],
            ['Literacy & math stations:', 'Games and small-group rotations to reinforce classroom skills.'],
            ['Progress checks:', 'Simple trackers so families see completed assignments and areas needing support.'],
          ].map(([k, v], i) => (
            <li key={`acad-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Enrichment & Clubs',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['STEM & makers:', 'Robotics, coding basics, engineering challenges, and maker projects.'],
            ['Arts & culture:', 'Visual arts, music, theater, and cultural exploration activities.'],
            ['Sports & wellness:', 'Team games, fitness circuits, yoga/mindfulness, and outdoor play.'],
          ].map(([k, v], i) => (
            <li key={`enrich-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Health, Nutrition & Safety',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Snacks & allergies:', 'Nutritious snacks with clear labeling and allergy-aware handling.'],
            ['Medication & first aid:', 'Trained staff, secure storage, and incident/illness notification protocols.'],
            ['Emergency readiness:', 'Drills for fire/severe weather/lockdown and site-specific safety plans.'],
          ].map(([k, v], i) => (
            <li key={`health-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Inclusion & Supports',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Accessibility:', 'Reasonable accommodations, visual schedules, and sensory-friendly options.'],
            ['Behavior supports:', 'Positive behavior frameworks, clear expectations, and restorative approaches.'],
            ['Language access:', 'Translated materials and interpreter support upon request.'],
          ].map(([k, v], i) => (
            <li key={`incl-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Staffing & Training',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Qualified staff:', 'Background-checked team members with youth development experience.'],
            ['Ongoing training:', 'Safety, first aid/CPR, behavior supports, and curriculum facilitation.'],
            ['Coach observation:', 'Regular walkthroughs and feedback to keep activities engaging and safe.'],
          ].map(([k, v], i) => (
            <li key={`staff-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Summer Programs & Camps',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Full-day options:', 'Multi-week sessions with field trips, theme weeks, and project showcases.'],
            ['Learning loss prevention:', 'Literacy/math enrichment woven into fun, hands-on activities.'],
            ['Registration timeline:', 'Priority re-enrollment for returning families; limited seats fill quickly.'],
          ].map(([k, v], i) => (
            <li key={`summer-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Family Communication & Events',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Updates & portals:', 'Weekly updates, calendars, and reminders via email/app where available.'],
            ['Showcase nights:', 'Student performances, project galleries, and family celebration events.'],
            ['Feedback channels:', 'Surveys and open-door policy for questions or concerns.'],
          ].map(([k, v], i) => (
            <li key={`family-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Transportation & Site Access',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['School-based sites:', 'Programs located on campus or nearby community facilities.'],
            ['Transportation options:', 'Limited bus routes or parent pickup depending on site; verify during enrollment.'],
            ['Late pickup policy:', 'Grace periods and clear fee/notification policies for late pickups.'],
          ].map(([k, v], i) => (
            <li key={`transport-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Quality & Outcomes',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Participation & growth:', 'Track attendance, engagement, and skill development over time.'],
            ['Academic indicators:', 'Homework completion and teacher/family feedback on progress.'],
            ['Continuous improvement:', 'Use data and input to refresh activities and address gaps.'],
          ].map(([k, v], i) => (
            <li key={`quality-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="After-School and Summer Programs" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">After-School and Summer Programs</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Safe, structured time after the bell—with homework help, enrichment, and healthy activities—so kids grow
                academically, socially, and physically while families have dependable care.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We provide engaging, inclusive programs that support whole-child development—partnering with families and
                schools to keep students safe, inspired, and on track year-round.
              </p>
            </div>

            <div>
              <Link to="/department/education">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default AfterSchoolPrograms;
